// frontend/src/components/p2p/TradeRow.tsx
import { ArrowRight, Check, Clock, X } from 'lucide-react'
import { P2PTrade } from '../../lib/api'
import { cn } from '../../lib/utils'
import { PendingLine, Timestamp, TxLine } from './tx-helpers'

interface Props {
  trade: P2PTrade
  myAddress: string
}

export function TradeRow({ trade, myAddress }: Props) {
  const isTaker = trade.takerAddress.toLowerCase() === myAddress.toLowerCase()

  return (
    <div className="group rounded-xl border border-dark-800 bg-dark-900/40 hover:border-dark-700 transition-colors p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <RoleBadge role={isTaker ? 'taker' : 'maker'} />
          <span className="font-mono text-[11px] text-dark-500" title={trade.tradeId}>
            {trade.tradeId.slice(0, 8)}…
          </span>
        </div>
        <TradeStatusPill status={trade.status} />
      </div>

      {/* Exchange visualization */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="text-[10px] font-mono text-dark-500 uppercase tracking-wider">w2usdt</div>
          <div className="font-display text-lg text-white tabular-nums">{formatHuman(trade.amountW2usdt)}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-dark-500 shrink-0" />
        <div className="flex-1 text-right">
          <div className="text-[10px] font-mono text-dark-500 uppercase tracking-wider">USDT</div>
          <div className="font-display text-lg text-white tabular-nums">{formatHuman(trade.amountUsdt)}</div>
        </div>
      </div>

      {/* Tx hashes per chain */}
      <div className="flex flex-col gap-1.5 mb-3">
        {trade.uni2TxHash ? <TxLine chain="Uni2" hash={trade.uni2TxHash} /> : <PendingLine chain="Uni2" />}
        {trade.bscTxHash ? <TxLine chain="BSC" hash={trade.bscTxHash} /> : <PendingLine chain="BSC" />}
      </div>

      {/* Timestamps + premium */}
      <div className="flex items-start justify-between gap-3 text-[11px] font-mono text-dark-500 pt-2 border-t border-dark-800/60">
        <div className="flex flex-col gap-0.5">
          <Timestamp label="Creado" iso={trade.createdAt} />
          <Timestamp label="Settled" iso={trade.settledAt ?? null} />
        </div>
        <span className={cn('shrink-0', trade.premiumBpsApplied >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
          Prima {trade.premiumBpsApplied >= 0 ? '+' : ''}
          {(trade.premiumBpsApplied / 100).toFixed(2)}%
        </span>
      </div>
    </div>
  )
}

function RoleBadge({ role }: { role: 'taker' | 'maker' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border tracking-wide',
        role === 'taker'
          ? 'bg-uni-500/10 text-uni-400 border-uni-500/30'
          : 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      )}
    >
      {role === 'taker' ? 'Taker' : 'Maker'}
    </span>
  )
}

function TradeStatusPill({ status }: { status: P2PTrade['status'] }) {
  const meta = {
    SETTLED: { icon: <Check className="w-3 h-3" />, text: 'text-uni-400', bg: 'bg-uni-500/10 border-uni-500/30', label: 'Settled' },
    PENDING: { icon: <Clock className="w-3 h-3" />, text: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', label: 'Pendiente' },
    PARTIAL_SETTLED: { icon: <Clock className="w-3 h-3" />, text: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30', label: 'Parcial' },
    FAILED: { icon: <X className="w-3 h-3" />, text: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/30', label: 'Failed' },
  }[status]
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border', meta.bg, meta.text)}>
      {meta.icon}
      {meta.label}
    </span>
  )
}

function formatHuman(wei: string, decimals = 18, maxFrac = 4): string {
  try {
    const n = BigInt(wei)
    const div = 10n ** BigInt(decimals)
    const whole = n / div
    const frac = n % div
    const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const fracStr = frac.toString().padStart(decimals, '0').slice(0, maxFrac).replace(/0+$/, '')
    return fracStr ? `${grouped}.${fracStr}` : grouped
  } catch {
    return '0'
  }
}
